import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import Loadable from "../layouts/full/shared/loadable/Loadable";
import { MOVED_ROUTES, RouterName } from "./RouterName";
import Moved from "./Moved";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import TrustedBy from "src/pages/trusted_by/view";
import WorkingProcess from "src/pages/working_process/view";
import Clients from "src/pages/clients/view";
import StatsNew from "src/pages/stats/view";
import Services from "src/pages/services/view";
import ContactUs from "src/pages/contact/view";
import FAQ from "src/pages/faq/view";
import RequestReviews from "src/pages/request_reviews/view";
import Requests from "src/pages/requests/view";
import Request from "src/pages/request/view";
import Cases from "src/pages/cases/inbox";
import Case from "src/pages/cases/case";
import Support from "src/pages/support/view";
import { homePath } from "src/layouts/full/shared/MenuItems";
import CountriesAwards from "src/pages/countriesAwards/view";
import UserAiChatSessions from "src/pages/user-ai-chat-sessions/view";

/* ***Layouts**** */
const FullLayout = Loadable(lazy(() => import("../layouts/full/FullLayout")));
const BlankLayout = Loadable(
  lazy(() => import("../layouts/blank/BlankLayout"))
);

/* ****Pages***** */
const Testimonials = Loadable(
  lazy(() => import("src/pages/testimonials/view"))
);
const Countries = Loadable(lazy(() => import("src/pages/countries/view")));
const CountryProfile = Loadable(
  lazy(() => import("src/pages/countries/profile/view"))
);
const CountriesGallery = Loadable(
  lazy(() => import("src/pages/countriesGallery/view"))
);
const CountriesPrices = Loadable(
  lazy(() => import("src/pages/countriesPrices/view"))
);
const CountryProducts = Loadable(lazy(() => import("src/pages/countriesPrices/byCountry")));
const GroupsPrices = Loadable(
  lazy(() => import("src/pages/groupsPrices/view"))
);
const Documents = Loadable(lazy(() => import("src/pages/documents/view")));

const ListDocuments = Loadable(
  lazy(() => import("src/pages/list_documents/view"))
);
const ListQuestions = Loadable(
  lazy(() => import("src/pages/list_questions/view"))
);
const RuleChanges = Loadable(lazy(() => import("src/pages/rules/RuleChanges")));
const VisaType = Loadable(lazy(() => import("src/pages/visa_type/view")));

const Groups = Loadable(lazy(() => import("src/pages/groups/view")));
const Nationality = Loadable(lazy(() => import("src/pages/nationality/view")));
const Questionnaires = Loadable(
  lazy(() => import("src/pages/questionnaires/view"))
);

const QuestionsCategory = Loadable(
  lazy(() => import("src/pages/questionnaires/questions_category/view"))
);

const VisaTypeDetails = Loadable(
  lazy(() => import("src/pages/countries/visa_type_country/view"))
);
const VisaTypeNationality = Loadable(
  lazy(() => import("src/pages/countries/visa_type_nationality/view"))
);
// A country's or group's FAQs, per visa type: written there or picked from
// the FAQ library (CMS review hala-25, sami-24).
const CountryFaqs = Loadable(lazy(() => import("src/pages/faq/CountryFaqs")));
const CountriesWorkingProcess = Loadable(
  lazy(() => import("src/pages/countries/visa_type_workingprocess/view"))
);

const AdminLogs = Loadable(lazy(() => import("src/pages/admins_logs/view")));

const Settings = Loadable(lazy(() => import("src/pages/settings/view")));
const SiteTexts = Loadable(lazy(() => import("src/pages/website-content/SiteTexts")));
const Waitlist = Loadable(lazy(() => import("src/pages/waitlist/view")));

const Money = Loadable(lazy(() => import("src/pages/money/view")));

const EcommerceDash = Loadable(
  lazy(() => import("../views/dashboard/Ecommerce"))
);

const AdminsPage = Loadable(lazy(() => import("../pages/admins/admins/view")));
const PrivilegesPage = Loadable(
  lazy(() => import("../pages/admins/privileges/view"))
);

/* ****Apps***** */

// authentication
const Login = Loadable(
  lazy(() => import("../views/authentication/auth/Login"))
);

const ForgotPassword = Loadable(
  lazy(() => import("../views/authentication/auth/ForgotPassword"))
);
const ResetPassword = Loadable(
  lazy(() => import("src/views/authentication/auth/ResetPassword"))
);

const Error = Loadable(lazy(() => import("../views/authentication/Error")));
const NotFoundInApp = Loadable(lazy(() => import("../views/authentication/NotFoundInApp")));
const Maintenance = Loadable(
  lazy(() => import("../views/authentication/Maintenance"))
);

let data = getLocalStorage(dataLocalStorage.privileges);

const Router = [
  {
    path: "/",
    element: <FullLayout />,
    children: [
      {
        path: "/",
        exact: true,
        element: (
          // Home if the person may see it, else the first page they can.
          <Navigate to={homePath()} replace />
        ),
      },

      data["1"]?.access_read == 1 && {
        path: RouterName.admins.admins,
        exact: true,
        element: <AdminsPage />,
      },
      // Roles and access has its own right (27, migration 026); before 026 a
      // role has only Staff's (1).
      (data["27"] ?? data["1"])?.access_read == 1 && {
        path: RouterName.admins.privileges,
        exact: true,
        element: <PrivilegesPage />,
      },

      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.countriesGallery,
        exact: true,
        element: <CountriesGallery />,
      },

      data["4"]?.access_read == 1 && {
        path: RouterName.awards,
        exact: true,
        element: <CountriesAwards />,
      },

      {
        path: RouterName.productDocuments,
        exact: true,
        element: <ListDocuments />,
      },
      {
        path: RouterName.productQuestions,
        exact: true,
        element: <ListQuestions />,
      },

      Number(data["4"]?.access_read) === 1 && {
        path: RouterName.ruleChanges,
        exact: true,
        element: <RuleChanges />,
      },

      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.countryProducts,
        exact: true,
        element: <CountryProducts />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.countriesPrices,
        exact: true,
        element: <CountriesPrices />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.groupsPrices,
        exact: true,
        element: <GroupsPrices />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.countries,
        exact: true,
        element: <Countries />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.countryProfile,
        exact: true,
        element: <CountryProfile />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.VisaTypeDetails,
        exact: true,
        element: <VisaTypeDetails />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.VisaTypeDetailsNationality,
        exact: true,
        element: <VisaTypeNationality />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.countriesFAQ,
        exact: true,
        element: <CountryFaqs />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.countriesWorkingProcess,
        exact: true,
        element: <CountriesWorkingProcess />,
      },

      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.groupsFAQ,
        exact: true,
        element: <CountryFaqs />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.countriesDetails.groupsWorkingProcess,
        exact: true,
        element: <CountriesWorkingProcess />,
      },

      data["9"]?.access_read == 1 && {
        path: RouterName.countriesDetails.visaType,
        exact: true,
        element: <VisaType />,
      },
      data["7"]?.access_read == 1 && {
        path: RouterName.countriesDetails.documents,
        exact: true,
        element: <Documents />,
      },
      data["5"]?.access_read == 1 && {
        path: RouterName.countriesDetails.groups,
        exact: true,
        element: <Groups />,
      },
      data["5"]?.access_read == 1 && {
        path: RouterName.countriesDetails.groupsPrices,
        exact: true,
        element: <Groups />,
      },
      data["8"]?.access_read == 1 && {
        path: RouterName.countriesDetails.nationality,
        exact: true,
        element: <Nationality />,
      },
      data["6"]?.access_read == 1 && {
        path: RouterName.countriesDetails.questionnairesByCategory,
        exact: true,
        element: <Questionnaires />,
      },

      data["6"]?.access_read == 1 && {
        path: RouterName.countriesDetails.questionnaires,
        exact: true,
        element: <Questionnaires />,
      },
      data["10"]?.access_read == 1 && {
        path: RouterName.countriesDetails.QuestionsCategory,
        exact: true,
        element: <QuestionsCategory />,
      },
      data["13"]?.access_read == 1 && {
        path: RouterName.trustedBy,
        exact: true,
        element: <TrustedBy />,
      },
      data["15"]?.access_read == 1 && {
        path: RouterName.testimonials,
        exact: true,
        element: <Testimonials />,
      },
      data["3"]?.access_read == 1 && {
        path: RouterName.clients,
        exact: true,
        element: <Clients />,
      },
      data["16"]?.access_read == 1 && {
        path: RouterName.working_process,
        exact: true,
        element: <WorkingProcess />,
      },
      data["17"]?.access_read == 1 && {
        path: RouterName.stats,
        exact: true,
        element: <StatsNew />,
      },
      data["14"]?.access_read == 1 && {
        path: RouterName.services,
        exact: true,
        element: <Services />,
      },
      data["18"]?.access_read == 1 && {
        path: RouterName.settings.settings,
        exact: true,
        element: <Settings />,
      },
      data["18"]?.access_read == 1 && {
        path: RouterName.websiteTexts,
        exact: true,
        element: <SiteTexts />,
      },
      data["19"]?.access_read == 1 && {
        path: RouterName.contact,
        exact: true,
        element: <ContactUs />,
      },
      data["19"]?.access_read == 1 && {
        path: RouterName.waitlist,
        exact: true,
        element: <Waitlist />,
      },
      data["21"]?.access_read == 1 && {
        path: RouterName.faq,
        exact: true,
        element: <FAQ />,
      },
      data["4"]?.access_read == 1 && {
        path: RouterName.request_reviews,
        exact: true,
        element: <RequestReviews />,
      },
      data["23"]?.access_read == 1 && {
        path: RouterName.cases,
        exact: true,
        element: <Cases />,
      },
      data["23"]?.access_read == 1 && {
        path: RouterName.case,
        exact: true,
        element: <Case />,
      },
      data["23"]?.access_read == 1 && {
        path: RouterName.requests,
        exact: true,
        element: <Requests />,
      },
      data["23"]?.access_read == 1 && {
        path: RouterName.request,
        exact: true,
        element: <Request />,
      },
      data["25"]?.access_read == 1 && {
        path: RouterName.support,
        exact: true,
        element: <Support />,
      },
      data["23"]?.access_read == 1 && {
        path: RouterName.userSessions,
        exact: true,
        element: <UserAiChatSessions />,
      },

      data["12"]?.access_read == 1 && {
        path: RouterName.admins_logs.admins_logs,
        exact: true,
        element: <AdminLogs />,
      },

      // -----------------------------------------------------------------------------
      // Privilege keys match the menu (MenuItems.js) and the names the API
      // gives them: 9 Visa type, 10 Questions category, 14 Services,
      // 24 Dashboard Stats, 25 Support Chat.
      data["24"]?.access_read == 1 && {
        path: "/dashboard",
        exact: true,
        element: <EcommerceDash />,
      },
      // Money reads the same figures as Home, under the same right.
      data["24"]?.access_read == 1 && {
        path: RouterName.money,
        exact: true,
        element: <Money />,
      },
      // Old addresses (bookmarks, links) go on to the new ones.
      ...MOVED_ROUTES.map(({ from, to }) => ({ path: from, element: <Moved to={to} /> })),
      { path: "/404", element: <NotFoundInApp /> },
      // An unknown address (or one this role cannot open) says so.
      { path: "*", element: <NotFoundInApp /> },
    ],
  },
  {
    path: "/",
    element: <BlankLayout />,
    children: [
      { path: "/auth/404", element: <Error /> },
      { path: RouterName.auth.login, element: <Login /> },
      { path: RouterName.auth.forgotPassword, element: <ForgotPassword /> },
      { path: RouterName.auth.resetPassword, element: <ResetPassword /> },
      { path: RouterName.auth.maintenance, element: <Maintenance /> },
      { path: "*", element: <Navigate to="/auth/404" /> },
    ],
  },
];

export default Router;
